import { Input } from "@/components/ui/input";
import { Euro } from "lucide-react";
import { Field } from "@/components/modelsheets/GeneralTab";
import { imageUrl } from "@/lib/modelSheets";
import { FabricCosts, fabricLines, useFabricPrices } from "@/components/modelsheets/FabricCosts";
import { AccessoryCosts, accessoryLines, useAccessoryPrices } from "@/components/modelsheets/AccessoryCosts";

const eur = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const num = (v) => (v === "" || v == null ? null : Number(v));


const Stat = ({ label, value, testid, strong }) => (
  <div className="rounded-lg border border-border bg-secondary/30 p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display ${strong ? "text-2xl text-primary" : "text-lg"}`} data-testid={testid}>{value}</p>
  </div>
);

export const StylistTab = ({ s, set, base }) => {
  const c = s.costs || {};
  const upd = (k) => (e) => set({ ...s, costs: { ...c, [k]: e.target.value } });
  const { data: auto = [] } = useFabricPrices(s, base);
  const lines = fabricLines(s, auto);
  const fabricCost = lines.reduce((t, l) => t + l.cost, 0);
  const { data: accAuto = [] } = useAccessoryPrices(s, base);
  const accLines = accessoryLines(s, accAuto);
  const accCost = accLines.reduce((t, l) => t + l.cost, 0);
  const cost = fabricCost + accCost + (Number(c.cut_cost) || 0) + (Number(c.sewing_cost) || 0);
  const setPrice = (k, v) => set({ ...s, costs: { ...c, fabric_prices: { ...(c.fabric_prices || {}), [k]: v } } });
  // I metri per capo li inserisce la stilista: non stanno nella scheda tecnica
  const setMeters = (i, v) => {
    const all = s.tech?.fabrics || [];
    const target = lines[i].f;
    set({ ...s, tech: { ...s.tech, fabrics: all.map((f) => (f === target ? { ...f, meters_per_piece: v } : f)) } });
  };
  const price = Number(c.sale_price) || 0;
  const img = imageUrl(s);
  return (
    <div className="min-w-0 space-y-6 py-4" data-testid="stylist-tab">
      <div className="flex items-center gap-4">
        {img && <img src={img} alt={s.name} className="h-20 w-20 rounded-md border border-border object-contain" />}
        <div>
          <p className="font-display text-lg">{s.name || "Senza nome"}</p>
          <p className="text-xs text-muted-foreground">{s.code || "Codice mancante"} · taglia base {s.base_size || "-"}</p>
        </div>
      </div>
      <div>
        <p className="mb-2 flex items-baseline justify-between font-display text-sm font-semibold">Costo tessuti<span data-testid="stylist-fabric-total">{eur(fabricCost)}</span></p>
        <FabricCosts lines={lines} onPrice={setPrice} onMeters={setMeters} />
        <p className="mt-1.5 text-[11px] text-muted-foreground">Il prezzo arriva dalle materie prime (il più basso per il tipo di tessuto): scrivi un prezzo per sostituirlo. Scrivi qui i metri per capo di ogni tessuto.</p>
      </div>
      <div>
        <p className="mb-2 flex items-baseline justify-between font-display text-sm font-semibold">Costo accessori<span data-testid="stylist-accessory-total">{eur(accCost)}</span></p>
        <AccessoryCosts lines={accLines} onPrice={setPrice} />
        <p className="mt-1.5 text-[11px] text-muted-foreground">Il prezzo arriva dalle materie prime (stesso nome accessorio). La quantità è quella della distinta base (es. 4 bottoni, 0,5 metri).</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Costo di taglio (€ a capo)"><Input type="number" min="0" step="0.01" value={c.cut_cost ?? ""} onChange={upd("cut_cost")} data-testid="stylist-cut-cost" /></Field>
        <Field label="Costo di confezione (€ a capo)"><Input type="number" min="0" step="0.01" value={c.sewing_cost ?? ""} onChange={upd("sewing_cost")} data-testid="stylist-sewing-cost" /></Field>
        <Field label="Prezzo di vendita (€)"><Input type="number" min="0" step="0.01" value={c.sale_price ?? ""} onChange={upd("sale_price")} data-testid="stylist-sale-price" /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Costo del capo" value={eur(cost)} testid="stylist-total-cost" strong />
        <Stat label="Ricarico" value={cost && price ? `x ${(price / cost).toLocaleString("it-IT", { maximumFractionDigits: 2 })}` : "-"} testid="stylist-markup" />
        <Stat label="Margine" value={price ? `${eur(price - cost)} (${Math.round(((price - cost) / price) * 100)}%)` : "-"} testid="stylist-margin" />
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Euro className="h-3.5 w-3.5" />Questi dati li vede solo chi ha accesso ai dati economici e non vengono mai stampati.</p>
    </div>
  );
};

export const costsPayload = (c = {}) => ({
  cut_cost: num(c.cut_cost), sewing_cost: num(c.sewing_cost), sale_price: num(c.sale_price),
  fabric_prices: Object.fromEntries(Object.entries(c.fabric_prices || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])),
});
